import {
  ActionItemStatus,
  IssuesFilterByOption,
  IssuesSortByOption,
  IssueStatus,
  MetricsFilterByOption,
  SortByDirection,
  TaskItemsFilterByOption,
  TaskItemsSortByOption,
  TaskItemStatus,
  TeamIssuesGroupByOption,
  TeamNetworkListObjectType,
  TeamOverviewSection,
  TeamTaskItemsGroupByOption,
  UserIssuesGroupByOption,
  UserMeetingsFilterByOption,
  UserStrategyFilterByOption,
  UserStrategyGroupByOption,
  UserStrategySortByOption,
  UserTaskItemsGroupByOption,
  type TeamStrategyFilterByOption,
  type UserFeedbackFilterByOption,
  type UserFeedbackGroupByOption,
  type UserFeedbackTab,
} from "../constants";

interface ISettingsData {
  homeSettings: IHomeSettings;
  sideMenuSettings: ISideMenuSettings;
  drawerSettings: IDrawerSettings;
  chatBotSettings: IChatBotSettings;
  teamOverviewSettings: ITeamOverviewSettings;
  teamStrategySettings: {
    [teamSlug: string]: ITeamStrategySettings | undefined;
  };
  teamImplementationSettings: {
    [teamSlug: string]: ITeamImplementationSettings | undefined;
  };
  teamMetricsSettings: {
    [teamSlug: string]: IMetricsSettings | undefined;
  };
  teamMeetingSettings: {
    [teamSlug: string]: ITeamMeetingSettings | undefined;
  };
  userStrategySettings: {
    [userGuid: string]: IUserStrategySettings | undefined;
  };
  userImplementationSettings: {
    [userGuid: string]: IUserImplementationSettings | undefined;
  };
  userMetricsSettings: {
    [userGuid: string]: IMetricsSettings | undefined;
  };
  userMeetingsSettings: {
    [userGuid: string]: IUserMeetingsSettings | undefined;
  };
  userFeedbackSettings: {
    [userGuid: string]: IUserFeedbackSettings | undefined;
  };
  searchSettings: ISearchSettings;
  dataGridSettings: {
    [dataGridId: string]: IDataGridSettings | undefined;
  };
  tableSettings: {
    [tableId: string]: ITableSettings | undefined;
  };
  featureFlags: {
    [featureFlag in FeatureFlag]: boolean;
  };
  coherenceReportSettings: ICoherenceReportSettings;
  dismissedMeetingNotifications: IDismissedMeetingNotification[];
}

type FeatureFlag = "surveys" | "snowfall" | "userFeedback";

interface IDismissedMeetingNotification {
  instanceGuid: string;
  timestamp: number;
}

interface IDrawerSettings {
  width: number;
}

interface IChatBotSettings {
  width: number;
  height: number;
}

interface ITeamOverviewSettings {
  networkListView: boolean | undefined;
  networkListViewFilterByOption: TeamNetworkListObjectType | undefined;
  expandedSection: TeamOverviewSection | undefined;
}

interface ITeamStrategySettings {
  tableView: boolean;
  filters: Record<TeamStrategyFilterByOption, string[]>;
}

interface ITeamImplementationSettings {
  tableView: boolean;
  taskItemsGroupByOption: TeamTaskItemsGroupByOption;
  taskItemsFilterByOption: TaskItemsFilterByOption;
  taskItemsFilterByStatusOptions: TaskItemStatus[] | null;
  taskItemsSortByOption: TaskItemsSortByOption;
  taskItemsSortByDirection: SortByDirection;
  issuesGroupByOption: TeamIssuesGroupByOption;
  issuesFilterByOption: IssuesFilterByOption;
  issuesFilterByStatusOptions: IssueStatus[] | null;
  issuesSortByOption: IssuesSortByOption;
  issuesSortByDirection: SortByDirection;
}

interface IMetricsSettings {
  filters: Record<MetricsFilterByOption, string[]>;
}

interface IUserStrategySettings {
  tableView: boolean;
  groupByOption: UserStrategyGroupByOption;
  filterByOption: UserStrategyFilterByOption;
  filterByStatusOptions: ActionItemStatus[] | null;
  sortByOption: UserStrategySortByOption;
  sortByDirection: SortByDirection;
}

interface IUserImplementationSettings {
  tableView: boolean;
  taskItemsGroupByOption: UserTaskItemsGroupByOption;
  taskItemsFilterByOption: TaskItemsFilterByOption;
  taskItemsFilterByStatusOptions: TaskItemStatus[] | null;
  taskItemsSortByOption: TaskItemsSortByOption;
  taskItemsSortByDirection: SortByDirection;
  issuesGroupByOption: UserIssuesGroupByOption;
  issuesFilterByOption: IssuesFilterByOption;
  issuesFilterByStatusOptions: IssueStatus[] | null;
  issuesSortByOption: IssuesSortByOption;
  issuesSortByDirection: SortByDirection;
}

type IUserMeetingsFilterValue = UserMeetingsFilterByOption;

interface IUserMeetingsSettings {
  filterValue: IUserMeetingsFilterValue | undefined;
}

interface IUserFeedbackSettings {
  tab: UserFeedbackTab;
  receivedFeedbackGroupByOption: UserFeedbackGroupByOption;
  receivedFeedbackFilters: Record<UserFeedbackFilterByOption, string[]>;
  givenFeedbackGroupByOption: UserFeedbackGroupByOption;
  givenFeedbackFilters: Record<UserFeedbackFilterByOption, string[]>;
}

interface ISideMenuSettings {
  isExpanded: boolean;
  expandedWidth: number;
}

interface IHomeSettings {
  showPeople: boolean;
  selectedTeamGuids: string[];
}

interface ISearchSettings {
  searchHistory: string[];
}

interface IDataGridSettings {
  columnVisibility: Record<string, boolean>;
}

interface ITableSettings {
  columnVisibility: Record<string, boolean>;
}

interface ICoherenceReportSettings {
  selectedTeamGuids: string[] | undefined;
  comparisonTeamGuids: string[] | undefined;
}

interface ITeamMeetingSettings {
  metrics: IMetricsSettings;
  strategy: ITeamStrategySettings;
}

const useLocalStorage = () => {
  function getItem<T>(key: string) {
    const serializedItem = localStorage.getItem(key);

    if (serializedItem === null) {
      return null;
    }

    try {
      return JSON.parse(serializedItem) as T;
    } catch {
      return null;
    }
  }

  function setItem<T>(key: string, item: T) {
    const serializedItem = JSON.stringify(item);
    localStorage.setItem(key, serializedItem);
  }

  function getLocalStorageSettings() {
    return getItem<ISettingsData>("settings");
  }

  function setLocalStorageSettings(settings: ISettingsData) {
    setItem("settings", settings);
  }

  return {
    getLocalStorageSettings,
    setLocalStorageSettings,
  };
};

export { useLocalStorage };

export type {
  FeatureFlag,
  IChatBotSettings,
  ICoherenceReportSettings,
  IDataGridSettings,
  IDismissedMeetingNotification,
  IDrawerSettings,
  IHomeSettings,
  IMetricsSettings,
  ISearchSettings,
  ISettingsData,
  ISideMenuSettings,
  ITableSettings,
  ITeamImplementationSettings,
  ITeamMeetingSettings,
  ITeamOverviewSettings,
  ITeamStrategySettings,
  IUserFeedbackSettings,
  IUserImplementationSettings,
  IUserMeetingsFilterValue,
  IUserMeetingsSettings,
  IUserStrategySettings,
};
